import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  IoArrowUndoOutline,
  IoAttachOutline,
  IoBuildOutline,
  IoCheckmarkOutline,
  IoChevronDownOutline,
  IoCloseOutline,
  IoCopyOutline,
  IoDownloadOutline,
  IoEyeOutline,
  IoFolderOutline,
  IoShareOutline,
  IoMicOutline,
  IoStopCircleOutline,
} from 'react-icons/io5';
import { PiCompassRose, PiCompassRoseBold } from 'react-icons/pi';

import SavedFileImage, { savedImageFileId } from './SavedFileImage';
import { viewerKind } from '../viewer/fileKinds';
import EmailApprovalCard from './EmailApprovalCard';
import AppActionApprovalCard from './AppActionApprovalCard';
import AppConnectCard from './AppConnectCard';
import VoiceRecordingStrip from './VoiceRecordingStrip';
import useVoiceRecorder, { appendTranscript } from '../hooks/useVoiceRecorder';
import {
  DEFAULT_CONTEXT_WINDOW_TOKENS,
  formatFileSize,
  formatTokenCompact,
  formatToolBadgeTooltip,
  getFileTypeIcon,
  getImageId,
  getToolBadgePresentation,
  isTransientToolActivity,
  isImageToken,
  isVizNameToken,
  normalizeToolBadgeStatus,
  rewriteCiteTags,
  splitByVizTokens,
  truncateBriefTitle,
} from './helpers';

const EXPORT_OPTIONS = [
  { id: 'brief', label: 'Sharable Brief', prompt: 'Create a brief from our conversation' },
  { id: 'pdf', label: 'PDF', prompt: 'Create a PDF from our conversation' },
  { id: 'word', label: 'Word Document', prompt: 'Create a Word document from our conversation' },
  { id: 'excel', label: 'Excel', prompt: 'Create an Excel spreadsheet from our conversation' },
];

export const AtlasContextMeter = React.memo(function AtlasContextMeter({ contextUsage }) {
  if (!contextUsage) return null;
  const windowTokens = (
    contextUsage.context_display_limit_tokens
    || contextUsage.context_soft_limit_tokens
    || contextUsage.context_window_tokens
    || DEFAULT_CONTEXT_WINDOW_TOKENS
  );
  const usedTokens = contextUsage.context_actual_used_tokens || contextUsage.context_used_tokens || 0;
  const displayUsedTokens = contextUsage.context_display_used_tokens || Math.min(windowTokens, usedTokens);
  const fillRatio = Math.max(
    0,
    Math.min(1, contextUsage.context_fill_ratio || (windowTokens > 0 ? displayUsedTokens / windowTokens : 0))
  );
  const fillPercent = Math.max(0, Math.min(100, fillRatio * 100));
  const isEstimated = Boolean(contextUsage.estimated);
  const label = `Context ${isEstimated ? '~' : ''}${formatTokenCompact(usedTokens)} / ${formatTokenCompact(windowTokens)}`;
  const statusText = isEstimated
    ? 'Context compacted - room freed (approximate until the turn completes).'
    : usedTokens > windowTokens
    ? 'Over limit - Atlas will compact before continuing.'
    : fillRatio < 0.7
    ? 'Plenty of room left.'
    : fillRatio < 0.9
    ? 'Getting full.'
    : 'Almost full - start a new chat soon.';

  return (
    <div className="atlas-context-meter" aria-label={`Context usage: ${label}`}>
      <div className="atlas-context-meter-track" aria-hidden="true">
        <div className="atlas-context-meter-fill" style={{ width: `${fillPercent}%` }} />
      </div>
      <div className="atlas-context-meter-popup">
        <div className="atlas-context-meter-popup-title">Conversation space</div>
        <div className="atlas-context-meter-popup-usage">{label}</div>
        <div className="atlas-context-meter-popup-status">{statusText}</div>
      </div>
    </div>
  );
});

export const AtlasExportMenu = React.memo(function AtlasExportMenu({ onExport, hasBrief, isMobile, disabled }) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  const options = hasBrief ? EXPORT_OPTIONS.filter((option) => option.id !== 'brief') : EXPORT_OPTIONS;

  useEffect(() => {
    if (!open) return undefined;
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const handleSelect = useCallback((option) => {
    setOpen(false);
    onExport(option.prompt);
  }, [onExport]);

  const showCompact = isMobile || hasBrief;

  return (
    <div className="atlas-export-menu" ref={menuRef}>
      <button
        type="button"
        className={`atlas-export-btn ${showCompact ? 'atlas-export-btn-compact' : ''}`}
        onClick={() => setOpen((prev) => !prev)}
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={open}
        title="Export this conversation as a brief, PDF, Word document or Excel file"
      >
        <IoShareOutline size={15} />
        <span>Export</span>
        <IoChevronDownOutline className={`atlas-export-chevron ${open ? 'atlas-export-chevron-open' : ''}`} />
      </button>
      {open && (
        <div className="atlas-export-dropdown">
          {options.map((option) => (
            <button
              key={option.id}
              type="button"
              className="atlas-export-dropdown-item"
              onClick={() => handleSelect(option)}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
});

// With a viewer (onOpen) the row opens the file and a separate icon downloads it.
function FilesPanelRow({ file, onOpen, onDownload }) {
  const download = () => onDownload({ fileId: file.file_id, filename: file.filename, source: file.source });
  const content = (
    <>
      {getFileTypeIcon(file.mime_type, file.filename)}
      <span className="atlas-files-row-name">{file.filename}</span>
      {file.size_bytes && <span className="atlas-files-row-size">{formatFileSize(file.size_bytes)}</span>}
    </>
  );
  if (!onOpen) {
    return (
      <button type="button" className="atlas-files-row" onClick={download} title={`Download ${file.filename}`}>
        {content}
        <IoDownloadOutline size={14} className="atlas-files-row-dl" />
      </button>
    );
  }
  return (
    <div className="atlas-files-row-wrap">
      <button type="button" className="atlas-files-row" onClick={() => onOpen(file)} title={`Open ${file.filename}`}>
        {content}
      </button>
      <button type="button" className="atlas-files-row-dl-btn" onClick={download} aria-label={`Download ${file.filename}`} title="Download">
        <IoDownloadOutline size={14} />
      </button>
    </div>
  );
}

export const ConversationFilesPanel = React.memo(function ConversationFilesPanel({
  files,
  isOpen,
  onToggle,
  onDownload,
  onOpen,
}) {
  const panelRef = useRef(null);
  const [uploadedOpen, setUploadedOpen] = useState(true);
  const [generatedOpen, setGeneratedOpen] = useState(true);
  const [retrievedOpen, setRetrievedOpen] = useState(true);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleClickOutside = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) onToggle();
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onToggle]);

  const filteredFiles = useMemo(
    // Hide SQL scratch outputs and superseded versions (a corrected re-save
    // marks its predecessor is_current_version=false — only show the current one).
    () => files.filter((file) => file.source !== 'sql_query' && file.is_current_version !== false),
    [files]
  );
  const uploadedFiles = useMemo(() => filteredFiles.filter((file) => file.source === 'upload'), [filteredFiles]);
  const retrievedFiles = useMemo(() => filteredFiles.filter((file) => file.source === 'return_file'), [filteredFiles]);
  const generatedFiles = useMemo(
    () => filteredFiles.filter((file) => file.source === 'save_file' || file.source === 'generate_image'),
    [filteredFiles]
  );

  const totalCount = filteredFiles.length;
  if (totalCount === 0) return null;

  return (
    <div className="atlas-files-panel" ref={panelRef}>
      <button
        type="button"
        className="atlas-files-badge-btn"
        onClick={onToggle}
        aria-label={`Conversation files: ${totalCount}`}
      >
        <IoFolderOutline size={16} />
        <span className="atlas-files-badge-count">{totalCount}</span>
      </button>

      {isOpen && (
        <div className="atlas-files-dropdown">
          <div className="atlas-files-dropdown-header">
            <span>Conversation Files</span>
          </div>

          {uploadedFiles.length > 0 && (
            <div className="atlas-files-section">
              <button
                type="button"
                className="atlas-files-section-header"
                onClick={() => setUploadedOpen((prev) => !prev)}
              >
                <IoChevronDownOutline
                  size={14}
                  className={`atlas-files-chevron ${uploadedOpen ? '' : 'atlas-files-chevron-collapsed'}`}
                />
                <span>Uploaded Files ({uploadedFiles.length})</span>
              </button>
              {uploadedOpen && (
                <div className="atlas-files-section-list">
                  {uploadedFiles.map((file) => (
                    <FilesPanelRow key={file.file_id} file={file} onOpen={onOpen} onDownload={onDownload} />
                  ))}
                </div>
              )}
            </div>
          )}

          {generatedFiles.length > 0 && (
            <div className="atlas-files-section">
              <button
                type="button"
                className="atlas-files-section-header"
                onClick={() => setGeneratedOpen((prev) => !prev)}
              >
                <IoChevronDownOutline
                  size={14}
                  className={`atlas-files-chevron ${generatedOpen ? '' : 'atlas-files-chevron-collapsed'}`}
                />
                <span>Generated Files ({generatedFiles.length})</span>
              </button>
              {generatedOpen && (
                <div className="atlas-files-section-list">
                  {generatedFiles.map((file) => (
                    <FilesPanelRow key={file.file_id} file={file} onOpen={onOpen} onDownload={onDownload} />
                  ))}
                </div>
              )}
            </div>
          )}

          {retrievedFiles.length > 0 && (
            <div className="atlas-files-section">
              <button
                type="button"
                className="atlas-files-section-header"
                onClick={() => setRetrievedOpen((prev) => !prev)}
              >
                <IoChevronDownOutline
                  size={14}
                  className={`atlas-files-chevron ${retrievedOpen ? '' : 'atlas-files-chevron-collapsed'}`}
                />
                <span>Retrieved Files ({retrievedFiles.length})</span>
              </button>
              {retrievedOpen && (
                <div className="atlas-files-section-list">
                  {retrievedFiles.map((file) => (
                    <FilesPanelRow key={file.file_id} file={file} onOpen={onOpen} onDownload={onDownload} />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
});

function getToolTraceCallCount(toolTraces) {
  if (!Array.isArray(toolTraces)) return 0;
  return toolTraces.reduce((total, trace) => {
    if (Array.isArray(trace?.calls)) {
      return total + trace.calls.filter((call) => !isTransientToolActivity(call?.tool_name || call?.name, call)).length;
    }
    if (Array.isArray(trace?.tool_names)) {
      return total + trace.tool_names.filter((name) => !isTransientToolActivity(name)).length;
    }
    return total;
  }, 0);
}

function formatTraceValue(value) {
  if (value == null || value === '') return 'None';
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return 'None';
    try {
      return JSON.stringify(JSON.parse(trimmed), null, 2);
    } catch {
      return value;
    }
  }
  return JSON.stringify(value, null, 2);
}

function getWebSearchResults(value) {
  if (!value || typeof value !== 'object') return [];
  if (Array.isArray(value.results)) return value.results;
  if (Array.isArray(value.output?.results)) return value.output.results;
  return [];
}

function renderToolOutputValue(call, value) {
  if (call?.tool_name === 'web_search') {
    const results = getWebSearchResults(value);
    if (results.length > 0) {
      return (
        <div className="atlas-web-search-results">
          {results.map((result, idx) => {
            const title = result?.title || result?.url || `Result ${idx + 1}`;
            const url = result?.url;
            return (
              <div key={`${url || title}_${idx}`} className="atlas-web-search-result">
                <div className="atlas-web-search-result-index">{result?.i || idx + 1}</div>
                <div className="atlas-web-search-result-body">
                  {url ? (
                    <a href={url} target="_blank" rel="noopener noreferrer">
                      {title}
                    </a>
                  ) : (
                    <span>{title}</span>
                  )}
                  {url ? <div className="atlas-web-search-result-url">{url}</div> : null}
                  {result?.page_age ? <div className="atlas-web-search-result-age">{result.page_age}</div> : null}
                </div>
              </div>
            );
          })}
        </div>
      );
    }
    if (value?.result_count != null) {
      return (
        <div className="atlas-tool-history-muted">
          This older web search trace only stored the result count ({value.result_count}), not the result list.
        </div>
      );
    }
  }
  return <pre>{formatTraceValue(value)}</pre>;
}

function AtlasToolTraceInspector({ message, isAdmin, onLoadToolTraceDetails }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const traces = Array.isArray(message?.toolTraces) ? message.toolTraces : [];
  const callCount = getToolTraceCallCount(traces) || (Array.isArray(message?.toolBadges) ? message.toolBadges.length : 0);

  if (!isAdmin || callCount <= 0) return null;

  const handleToggle = async () => {
    const nextOpen = !open;
    setOpen(nextOpen);
    if (!nextOpen || message?.toolTraceDetailsLoaded || !message?.dbMessageId || !onLoadToolTraceDetails) return;
    setLoading(true);
    setError('');
    try {
      await onLoadToolTraceDetails(message);
    } catch (err) {
      setError(err?.message || String(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="atlas-tool-history">
      <button
        type="button"
        className="atlas-tool-history-toggle"
        onClick={handleToggle}
        disabled={loading}
      >
        <IoChevronDownOutline className={`atlas-tool-history-chevron ${open ? 'atlas-tool-history-chevron-open' : ''}`} />
        <IoBuildOutline size={14} />
        <span>Tool history ({callCount})</span>
      </button>
      {open && (
        <div className="atlas-tool-history-panel">
          {loading ? <div className="atlas-tool-history-muted">Loading tool details...</div> : null}
          {error ? <div className="atlas-tool-history-error">{error}</div> : null}
          {!loading && !error && traces.length === 0 ? (
            <div className="atlas-tool-history-muted">
              {message?.dbMessageId ? 'No stored trace details found.' : 'Details are available after this response is saved.'}
            </div>
          ) : null}
          {!loading && !error && traces.map((trace) => {
            const calls = Array.isArray(trace?.calls)
              ? trace.calls.filter((call) => !isTransientToolActivity(call?.tool_name || call?.name, call))
              : [];
            if (calls.length === 0) return null;
            return (
              <div key={trace.trace_id || `${message.id}_trace`} className="atlas-tool-trace">
                <div className="atlas-tool-trace-header">
                  <span>{calls.length} call{calls.length === 1 ? '' : 's'}</span>
                  {trace.total_duration_ms ? <span>{trace.total_duration_ms} ms</span> : null}
                  {trace.error_count ? <span className="atlas-tool-trace-error-count">{trace.error_count} retried</span> : null}
                </div>
                <div className="atlas-tool-call-list">
                  {calls.map((call, idx) => {
                    const outputValue = call.output !== undefined ? call.output : call.output_summary;
                    const toolName = call.tool_name || 'tool';
                    const presentation = getToolBadgePresentation(toolName, toolName);
                    const displayName = presentation.label || toolName;
                    return (
                      <details key={`${trace.trace_id || message.id}_${idx}`} className="atlas-tool-call-detail">
                        <summary>
                          <span className="atlas-tool-call-name" title={toolName}>{displayName}</span>
                          <span className={`atlas-tool-call-status atlas-tool-call-status-${call.status || 'unknown'}`}>
                            {call.status === 'error' ? 'retried' : call.status || 'unknown'}
                          </span>
                        </summary>
                        <div className="atlas-tool-call-grid">
                          <div className="atlas-tool-call-block">
                            <div className="atlas-tool-call-label">Input</div>
                            <pre>{formatTraceValue(call.input)}</pre>
                          </div>
                          <div className="atlas-tool-call-block">
                            <div className="atlas-tool-call-label">Output</div>
                            {renderToolOutputValue(call, outputValue)}
                          </div>
                          {call.error ? (
                            <div className="atlas-tool-call-block atlas-tool-call-block-wide">
                              <div className="atlas-tool-call-label">Error</div>
                              <pre>{formatTraceValue(call.error)}</pre>
                            </div>
                          ) : null}
                        </div>
                      </details>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export const AtlasMessagesList = React.memo(function AtlasMessagesList({
  messages,
  currentAssistantId,
  lastAssistantMsgRef,
  activity,
  reasoningTrace,
  queuedDrafts,
  onRemoveQueuedDraft,
  onSendQueuedNow,
  activityKey,
  activityNowTs,
  openBriefById,
  downloadUploadedFile,
  downloadSavedFile,
  previewSavedFile,
  openDocument,
  copiedMessageId,
  onCopyMessage,
  VizEmbed,
  ImageEmbed,
  onNavigateConversation,
  user,
  conversationId,
  onRollbackMessage,
  onLoadToolTraceDetails,
}) {
  const imageMessagesRef = useRef(messages);
  imageMessagesRef.current = messages;
  // Custom link renderer: intercept /c/{id} links for in-app navigation
  const markdownComponents = React.useMemo(() => ({
    img: ({ src, alt }) => {
      const fileId = savedImageFileId(src);
      if (!fileId) return <img src={src} alt={alt || ""} loading="lazy" />;
      const file = (imageMessagesRef.current || []).flatMap((m) => m.savedFiles || []).find((f) => f.file_id === fileId);
      return <SavedFileImage fileId={fileId} file={file} label={alt || file?.filename || "Photo"} loadPreview={previewSavedFile} />;
    },
    a: ({ href, children, ...props }) => {
      const match = href && href.match(/^\/c\/([0-9a-f-]{36})$/);
      if (match && onNavigateConversation) {
        return (
          <a
            href={href}
            onClick={(e) => {
              e.preventDefault();
              onNavigateConversation(match[1]);
            }}
            {...props}
          >
            {children}
          </a>
        );
      }
      return <a href={href} target="_blank" rel="noopener noreferrer" {...props}>{children}</a>;
    },
  }), [onNavigateConversation, previewSavedFile]);
  if (!Array.isArray(messages) || messages.length === 0) return null;

  return (
    <>
      {messages.map((message, msgIdx) => {
        const isLastAssistant = message.role === 'assistant' && msgIdx === messages.length - 1;
        return (
          <div
            key={message.id}
            className={`atlas-message atlas-message-${message.role}`}
            ref={isLastAssistant ? lastAssistantMsgRef : null}
          >
            <div className="atlas-message-wrapper">
              <div className="atlas-bubble">
                {message.role !== 'assistant' ? (
                  <>
                    {Array.isArray(message.uploadedFiles) && message.uploadedFiles.length > 0 && (
                      <div className="atlas-user-attached-files">
                        {message.uploadedFiles.map((file) => {
                          // A failed upload keeps its pending id; it must not read as "Uploading…" forever.
                          const isFailed = file.upload_status === 'failed';
                          const isPending = !isFailed && String(file.file_id || '').startsWith('pending_');
                          if (file.kind === 'land_evaluation') {
                            return (
                              <span key={file.file_id} className="atlas-user-attached-file" title={`Land Evaluation ${file.request_id || ''}`.trim()}>
                                <span className="atlas-user-file-icon">🗺️</span>
                                <span className="atlas-user-file-name" title={file.filename}>{file.filename}</span>
                                <span className="atlas-user-file-size">Land Evaluation</span>
                              </span>
                            );
                          }
                          return (
                            <button
                              key={file.pending_id || file.file_id}
                              type="button"
                              className={`atlas-user-attached-file${isFailed ? ' is-failed' : ''}`}
                              onClick={() => {
                                if (isPending || isFailed) return;
                                if (openDocument) openDocument({ ...file, viewerOrigin: 'upload' });
                                else downloadUploadedFile({ fileId: file.file_id, filename: file.filename });
                              }}
                              title={isFailed
                                ? `${file.filename} was not sent: ${file.upload_error || 'upload failed'}`
                                : isPending ? 'Uploading...' : `${openDocument ? 'Open' : 'Download'} ${file.filename}`}
                              disabled={isPending || isFailed}
                              aria-disabled={isPending || isFailed}
                              style={isPending ? { opacity: 0.6, cursor: 'wait' } : undefined}
                            >
                              <span className="atlas-user-file-icon">{getFileTypeIcon(file.mime_type, file.filename)}</span>
                              <span className="atlas-user-file-name" title={file.filename}>
                                {file.filename}
                              </span>
                              {isFailed
                                ? <span className="atlas-user-file-size atlas-user-file-failed">Not sent</span>
                                : file.size_bytes ? <span className="atlas-user-file-size">{formatFileSize(file.size_bytes)}</span> : null}
                            </button>
                          );
                        })}
                      </div>
                    )}
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.content || ''}</ReactMarkdown>
                  </>
                ) : (
                  <div className="atlas-message-body">
                    <div className="atlas-assistant-top">
                      <div className="atlas-assistant-top-text">
                        <div className="atlas-assistant-name-row">
                          <div className={!message.finalized ? 'atlas-compass-animating' : ''}>
                            <div className="atlas-compass-stack">
                              <PiCompassRose className="atlas-compass-light" />
                              <PiCompassRoseBold className="atlas-compass-bold" />
                            </div>
                          </div>
                          <span className="atlas-assistant-name">Atlas</span>
                        </div>
                        {!message.finalized && message.id === currentAssistantId ? (
                          <div
                            className={[
                              'atlas-assistant-status',
                              activity?.kind ? `atlas-assistant-status-${activity.kind}` : '',
                            ].filter(Boolean).join(' ')}
                          >
                            <span key={activityKey} className="atlas-assistant-status-text">
                              {activity?.icon && <span className="atlas-assistant-status-icon">{activity.icon}</span>}
                              {activity?.text || 'Atlas is working…'}
                              {activity?.spinning && activity?.startedAt
                                ? ` · ${Math.max(0, Math.floor((activityNowTs - activity.startedAt) / 1000))}s`
                                : ''}
                            </span>
                          </div>
                        ) : null}
                        {!message.finalized && message.id === currentAssistantId && reasoningTrace ? (
                          <div className="atlas-reasoning-trace" title={reasoningTrace}>
                            {reasoningTrace}
                          </div>
                        ) : null}
                      </div>
                    </div>

                    {message.briefId && message.finalized && (
                      <button
                        type="button"
                        className="atlas-inline-brief-link"
                        onClick={() => openBriefById(message.briefId)}
                        title={message.briefTitle || 'View Brief'}
                      >
                        <IoAttachOutline className="atlas-inline-brief-icon" />
                        <span className="atlas-inline-brief-label">Brief</span>
                        <span className="atlas-inline-brief-divider">|</span>
                        <span className="atlas-inline-brief-title">
                          {truncateBriefTitle(message.briefTitle, 24) || 'Untitled'}
                        </span>
                      </button>
                    )}

                    {Array.isArray(message.toolBadges) && message.toolBadges.length > 0 && (
                      <div className="atlas-tool-badges" aria-label="Tools used">
                        {message.toolBadges.map((badge) => {
                          const badgeKey = badge.badgeKey || badge.toolName;
                          const presentation = getToolBadgePresentation(badge.toolName, badgeKey);
                          const badgeIcon = presentation.badgeIcon || <IoBuildOutline size={18} />;
                          const running = (badge.runningCount || 0) > 0;
                          // A failed call shows as finished: Atlas usually recovers on the
                          // next call, and a red "!" read as the product breaking.
                          const rawStatus = normalizeToolBadgeStatus(badge.lastStatus, { running });
                          const status = rawStatus === 'error' ? 'done' : rawStatus;
                          const displayLabel = badge.displayLabel || presentation.label || badge.toolName;
                          const tooltipText = formatToolBadgeTooltip(displayLabel, badge);

                          return (
                            <div
                              key={badge.id}
                              className={[
                                'atlas-tool-badge',
                                running ? 'atlas-tool-badge-running' : '',
                                status === 'done' ? 'atlas-tool-badge-done' : '',
                                status === 'interrupted' ? 'atlas-tool-badge-interrupted' : '',
                              ].filter(Boolean).join(' ')}
                              aria-label={tooltipText}
                            >
                              <span className="atlas-tool-badge-icon" aria-hidden="true">
                                {badgeIcon}
                              </span>
                              {badge.count > 1 ? <span className="atlas-tool-badge-count">{badge.count}</span> : null}
                              {!running && status === 'done' ? <span className="atlas-tool-badge-check">✓</span> : null}
                              <span className="atlas-tool-badge-tooltip" aria-hidden="true">
                                {tooltipText}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    <AtlasToolTraceInspector
                      message={message}
                      isAdmin={!!user?.isAdmin}
                      onLoadToolTraceDetails={onLoadToolTraceDetails}
                    />

                    {splitByVizTokens(message.content || '')
                      .flatMap((part) => {
                        if (part.type !== 'markdown') return [part];
                        const lastTokenStart = part.content.lastIndexOf('<<<');
                        if (lastTokenStart === -1) return [part];
                        const after = part.content.slice(lastTokenStart);
                        if (after.includes('>>>')) return [part];
                        const prefix = part.content.slice(0, lastTokenStart);
                        return prefix ? [{ type: 'markdown', content: prefix }] : [];
                      })
                      .map((part, idx) => {
                        if (part.type === 'markdown') {
                          const markdown = rewriteCiteTags(part.content, message.citationsMap);
                          return (
                            <ReactMarkdown key={`${message.id}_md_${idx}`} remarkPlugins={[remarkGfm]} components={markdownComponents}>
                              {markdown}
                            </ReactMarkdown>
                          );
                        }
                        if (part.type === 'token' && isVizNameToken(part.token)) {
                          return <VizEmbed key={`${message.id}_viz_${idx}`} name={part.token} />;
                        }
                        if (part.type === 'token' && isImageToken(part.token)) {
                          return <ImageEmbed key={`${message.id}_img_${idx}`} imageId={getImageId(part.token)} />;
                        }
                        return (
                          <ReactMarkdown key={`${message.id}_raw_${idx}`} remarkPlugins={[remarkGfm]} components={markdownComponents}>
                            {rewriteCiteTags(part.raw, message.citationsMap)}
                          </ReactMarkdown>
                        );
                      })}
                  </div>
                )}
              </div>

              {Array.isArray(message.savedFiles) && message.savedFiles.length > 0 && message.finalized && (
                <div className="atlas-saved-files">
                  <div className="atlas-saved-files-label">
                    <IoDownloadOutline size={14} />
                    <span>Files ready for download</span>
                  </div>
                  <div className="atlas-saved-files-list">
                    {message.savedFiles.map((file) => {
                      const badge = file.version_badge;
                      const isSuperseded = badge && !badge.isLatest;
                      const isPhoto = String(file.mime_type || '').startsWith('image/');
                      // Clicking the file downloads it; the eye button opens it in the side
                      // panel. Files the viewer can't show (archives, unknown types) get no eye.
                      const canView = Boolean(openDocument) && viewerKind(file) !== 'none';
                      const viewFile = () => openDocument({ ...file, viewerOrigin: 'saved' });
                      return (
                        <div key={file.file_id} className={`atlas-saved-file-item${isPhoto ? ' atlas-saved-file-photo' : ''}${canView ? ' has-viewer' : ''}`}>
                          {isPhoto && (
                            <SavedFileImage fileId={file.file_id} file={file} label={file.filename || 'Photo'} loadPreview={previewSavedFile} />
                          )}
                        <button
                          type="button"
                          className={`atlas-saved-file-btn${isSuperseded ? ' atlas-saved-file-btn-superseded' : ''}`}
                          onClick={() => downloadSavedFile({ fileId: file.file_id, filename: file.filename, file })}
                          title={
                            isSuperseded
                              ? `Older version (v${badge.version}) — a newer revision exists later in this conversation`
                              : (file.description || `Download ${file.filename}`)
                          }
                        >
                          {getFileTypeIcon(file.mime_type, file.filename)}
                          <span className="atlas-saved-file-name">{file.filename}</span>
                          {badge && (
                            <span className={`atlas-saved-file-version ${badge.isLatest ? 'is-latest' : 'is-superseded'}`}>
                              {badge.isLatest ? `v${badge.version} · latest` : `v${badge.version} · superseded`}
                            </span>
                          )}
                          {file.size_bytes && <span className="atlas-saved-file-size">{formatFileSize(file.size_bytes)}</span>}
                          {isPhoto && <span className="atlas-photo-download-label">Download</span>}
                          <IoDownloadOutline size={16} className="atlas-saved-file-download-icon" />
                        </button>
                        {canView && (
                          <button
                            type="button"
                            className="atlas-saved-file-view-btn"
                            onClick={viewFile}
                            aria-label={`View ${file.filename}`}
                            title="View in the side panel"
                          >
                            <IoEyeOutline size={17} />
                          </button>
                        )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {Array.isArray(message.emailApprovals) && message.emailApprovals.map((approval) => (
                approval && approval.actionId ? (
                  <EmailApprovalCard key={approval.actionId} approval={approval} />
                ) : null
              ))}

              {Array.isArray(message.appApprovals) && message.appApprovals.map((approval) => (
                approval && approval.actionId ? (
                  <AppActionApprovalCard key={approval.actionId} approval={approval} />
                ) : null
              ))}

              {Array.isArray(message.appConnects) && message.appConnects.map((approval) => (
                approval && approval.actionId ? (
                  <AppConnectCard key={approval.actionId} approval={approval} />
                ) : null
              ))}

              {message.interrupted && message.finalized && (
                <div className="atlas-interrupted-note">Response stopped by user.</div>
              )}

              <div className="atlas-bubble-actions">
                <button
                  type="button"
                  className={`atlas-bubble-copy-btn ${copiedMessageId === message.id ? 'atlas-bubble-copy-btn-copied' : ''}`}
                  onClick={() => onCopyMessage(message.id, message.content)}
                  title={copiedMessageId === message.id ? 'Copied!' : 'Copy message'}
                >
                  {copiedMessageId === message.id ? (
                    <IoCheckmarkOutline className="atlas-bubble-copy-icon" />
                  ) : (
                    <IoCopyOutline className="atlas-bubble-copy-icon" />
                  )}
                  <span>{copiedMessageId === message.id ? 'Copied' : 'Copy'}</span>
                </button>
                {user?.isAdmin && message.role === 'assistant' && message.dbMessageId && message.finalized !== false && onRollbackMessage && conversationId && msgIdx < messages.length - 1 && (
                  <button
                    type="button"
                    className="atlas-bubble-rollback-btn"
                    onClick={() => {
                      const afterCount = messages.length - msgIdx - 1;
                      if (window.confirm(`Roll back to this message? ${afterCount} message${afterCount === 1 ? '' : 's'} after this will be deleted.`)) {
                        onRollbackMessage(message.dbMessageId, msgIdx);
                      }
                    }}
                    title="Roll back to this message (admin)"
                  >
                    <IoArrowUndoOutline className="atlas-bubble-copy-icon" />
                    <span>Rollback</span>
                  </button>
                )}
                {message.createdAt && (
                  <span className="atlas-bubble-timestamp">
                    {new Date(message.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}{', '}
                    {new Date(message.createdAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                  </span>
                )}
              </div>
            </div>
          </div>
        );
      })}
      {/* Queued messages sit in the conversation, below the in-progress
          response — they are user messages that have not been sent yet, so
          they belong in the flow rather than pinned to the composer. */}
      {(queuedDrafts || []).map((draft) => (
        <div key={draft.id} className="atlas-message atlas-message-user atlas-message-queued">
          <div className="atlas-message-wrapper">
            <div className="atlas-bubble atlas-bubble-queued">
              <div className="atlas-message-body">{draft.text}</div>
            </div>
            <div className="atlas-queued-meta">
              <span className="atlas-queued-meta-label">Queued · sends when Atlas finishes</span>
              {onSendQueuedNow && (
                <button
                  type="button"
                  className="atlas-queued-action"
                  onClick={() => onSendQueuedNow(draft.id)}
                  title="Stop the current response and start on this now"
                >
                  Send now
                </button>
              )}
              <button
                type="button"
                className="atlas-queued-action"
                onClick={() => onRemoveQueuedDraft?.(draft.id)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      ))}
    </>
  );
});

export const AtlasInputArea = React.memo(React.forwardRef(function AtlasInputArea({
  onSend,
  onStop,
  onQueue,
  onFileChange,
  onTranscribe,
  isStreaming,
  isReadOnly,
  isMobile,
  onScrollToBottom,
  placeholder,
  sendLabel,
}, ref) {
  const [input, setInput] = useState('');
  const textareaRef = useRef(null);
  const formRef = useRef(null);
  const fileInputRef = useRef(null);
  const voiceDoneRef = useRef(null);

  // Dictation fills the composer; the user reviews and sends it themselves.
  const handleVoiceText = useCallback((text) => {
    setInput((previous) => appendTranscript(previous, text));
    if (isMobile) return; // focusing would raise the keyboard over the text
    requestAnimationFrame(() => {
      const textarea = textareaRef.current;
      if (!textarea) return;
      textarea.focus();
      const end = textarea.value.length;
      textarea.setSelectionRange(end, end);
    });
  }, [isMobile]);
  const voice = useVoiceRecorder({ onTranscribe, onText: handleVoiceText });
  const showMic = Boolean(onTranscribe) && voice.supported;
  const isRecording = voice.status === 'recording';
  const isTranscribing = voice.status === 'transcribing';
  const { clearError: clearVoiceError, error: voiceError } = voice;

  useEffect(() => {
    if (isRecording) voiceDoneRef.current?.focus();
  }, [isRecording]);

  useEffect(() => {
    if (!voiceError) return undefined;
    const timer = setTimeout(clearVoiceError, 6000);
    return () => clearTimeout(timer);
  }, [voiceError, clearVoiceError]);

  const canSend = input.trim() && !isStreaming && !isReadOnly;
  // While Atlas is responding the composer stays live: Enter/Steer injects
  // into the running turn, Queue holds the message for after it finishes.
  const canQueue = Boolean(input.trim() && isStreaming && !isReadOnly && onQueue);

  const adjustTextareaHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = 'auto';
    const atlasMain = textarea.closest('.atlas-chat-main');
    const maxHeight = atlasMain ? atlasMain.clientHeight * 0.6 : window.innerHeight * 0.4;
    textarea.style.height = `${Math.min(textarea.scrollHeight, maxHeight)}px`;
  }, []);

  // The textarea unmounts while recording, so re-fit it when it comes back.
  useEffect(() => {
    adjustTextareaHeight();
  }, [input, isRecording, adjustTextareaHeight]);

  const doSend = useCallback(() => {
    const text = input.trim();
    if (!text || isStreaming || isReadOnly) return;
    onSend(text);
    setInput('');
  }, [input, isReadOnly, isStreaming, onSend]);

  const doQueue = useCallback(() => {
    const text = input.trim();
    if (!text || !isStreaming || isReadOnly || !onQueue) return;
    onQueue(text);
    setInput('');
  }, [input, isReadOnly, isStreaming, onQueue]);

  const handleFileInputChange = useCallback((event) => {
    const files = Array.from(event.target.files || []);
    if (files.length > 0) onFileChange(files);
    event.target.value = '';
  }, [onFileChange]);

  React.useImperativeHandle(ref, () => ({
    setInputAndSubmit(text) {
      const trimmed = (text || '').trim();
      if (trimmed) onSend(trimmed);
      setInput('');
    },
    setInputText(text) {
      setInput(text || '');
      requestAnimationFrame(() => {
        textareaRef.current?.focus();
      });
    },
    focus() {
      textareaRef.current?.focus();
    },
  }), [onSend]);

  return (
    <form
      ref={formRef}
      className={`atlas-input atlas-input-form${isRecording ? ' atlas-input-recording' : ''}`}
      onSubmit={(event) => {
        event.preventDefault();
        doSend();
      }}
      onKeyDown={(event) => {
        if (isRecording && event.key === 'Escape') {
          event.preventDefault();
          voice.cancel();
        }
      }}
    >
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept=".pdf,.txt,.md,.csv,.json,.tsv,.xlsx,.xls,.docx,.pptx,.jpg,.jpeg,.png,.gif,.webp,.svg"
        style={{ display: 'none' }}
        onChange={handleFileInputChange}
      />
      {isRecording ? (
        <>
          <button
            type="button"
            className="atlas-attach-btn atlas-voice-cancel"
            onClick={voice.cancel}
            aria-label="Discard recording"
            title="Discard recording (Esc)"
          >
            <IoCloseOutline size={20} />
          </button>
          <VoiceRecordingStrip analyser={voice.analyser} startedAt={voice.startedAt} maxMs={voice.maxMs} />
          <button
            ref={voiceDoneRef}
            type="button"
            className="atlas-voice-done"
            onClick={voice.finish}
            aria-label="Stop recording and transcribe"
            title="Done: turn it into text"
          >
            <IoCheckmarkOutline size={20} />
          </button>
        </>
      ) : (
        <>
          <button
            type="button"
            className="atlas-attach-btn"
            onClick={() => fileInputRef.current?.click()}
            disabled={isStreaming || isReadOnly}
            title="Attach files"
          >
            <IoAttachOutline size={20} />
          </button>
          <textarea
            ref={textareaRef}
            className="atlas-textbox"
            value={input}
            onChange={(event) => setInput(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                if (canSend) doSend();
                else if (canQueue) doQueue();
              }
            }}
            onFocus={() => {
              if (isMobile && onScrollToBottom) {
                setTimeout(() => onScrollToBottom(), 300);
              }
            }}
            placeholder={
              isTranscribing ? 'Turning your recording into text…'
                : isStreaming && !isReadOnly ? 'Queue a message while Atlas works…'
                  : (placeholder || 'Ask Atlas…')
            }
            disabled={isReadOnly}
            rows={1}
          />
          {showMic && (
            <span className="atlas-mic-wrap">
              <button
                type="button"
                className={`atlas-attach-btn atlas-mic-btn${isTranscribing ? ' atlas-mic-busy' : ''}`}
                onClick={() => {
                  voice.clearError();
                  voice.start();
                }}
                disabled={isReadOnly || voice.status !== 'idle'}
                aria-label={isTranscribing ? 'Transcribing' : 'Talk to Atlas'}
                title={isTranscribing ? 'Transcribing…' : 'Talk to Atlas: speak, then review the text before sending'}
              >
                {isTranscribing ? <span className="atlas-mic-spinner" aria-hidden="true" /> : <IoMicOutline size={20} />}
              </button>
              {voiceError && (
                <span className="atlas-voice-notice" role="alert">
                  {voiceError}
                </span>
              )}
            </span>
          )}
        </>
      )}
      {isRecording ? null : isStreaming ? (
        <>
          {canQueue && (
            <button className="atlas-btn atlas-steer-btn atlas-composer-action" type="button" onClick={doQueue}>
              Queue
              <span className="atlas-composer-tooltip" role="tooltip">
                Waits for Atlas to finish, then sends automatically. (Enter)
              </span>
            </button>
          )}
          <button
            className="atlas-icon-btn atlas-stop-btn atlas-composer-action"
            type="button"
            onClick={() => onStop()}
            aria-label="Stop generating"
          >
            <IoStopCircleOutline size={19} />
            <span className="atlas-composer-tooltip" role="tooltip">
              Stops the response where it is. Atlas keeps what it has so far.
            </span>
          </button>
        </>
      ) : (
        <button className="atlas-btn" type="submit" disabled={!canSend}>
          {sendLabel || 'Send'}
        </button>
      )}
    </form>
  );
}));
